import { Injectable } from '@angular/core';
import {
  MatLegacyDialog as MatDialog,
  MatLegacyDialogConfig as MatDialogConfig,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { DialogEditDeviceComponent } from '@app/modules/smart-devices/dialogs/dialog-edit-device/dialog-edit-device.component';
import { DeviceDetectionService } from '@app/shared/services/device-detection/device-detection.service';
import { LoadingSpinnerComponent } from '@ui';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Code, Device, MatchedDevice, UnmatchedDevice } from '../data/smart-devices.interface';
import { SmartDevicesService } from '../service/smart-devices.service';
import { DialogAddManualSmartCodeComponent } from './dialog-add-manual-smart-code/dialog-add-manual-smart-code.component';
import { DialogCodeReservationDetailsComponent } from './dialog-code-reservation-details/dialog-code-reservation-details.component';
import { DialogDeviceDetailsComponent } from './dialog-device-details/dialog-device-details.component';

@Injectable({ providedIn: 'root' })
export class SmartDevicesDialogService {
  mobile = false;

  dialogOptions: MatDialogConfig;

  constructor(
    private dialog: MatDialog,
    private smartDeviceService: SmartDevicesService,
    private deviceDetectionService: DeviceDetectionService
  ) {
    this.deviceDetectionService.isMobileViewport().subscribe((isMobile) => {
      this.mobile = isMobile;

      this.dialogOptions = {
        width: this.mobile ? '100%' : '720px',
        height: this.mobile ? '100%' : 'auto',
        minWidth: this.mobile ? '100%' : '720px',
        maxHeight: this.mobile ? '100vh' : '90vh',
      };
    });
  }

  openCodeDialog(reservationCode: string) {
    // Open loading indicator dialog
    const loadingRef: MatDialogRef<LoadingSpinnerComponent> = this.dialog.open(LoadingSpinnerComponent, {
      disableClose: true,
    });

    this.smartDeviceService.getReservationDetails(reservationCode).subscribe(
      (res) => {
        loadingRef.close();

        // Refresh background data
        this.smartDeviceService.refreshReservationsAndDevicesFromApi$.next(null);

        // Open the main dialog with the fetched data
        const dialog = this.dialog.open(DialogCodeReservationDetailsComponent, {
          ...this.dialogOptions,
          data: {
            reservation: res,
          },
        });

        dialog.afterClosed().subscribe((result) => {
          if (result && result.openDeviceModal) {
            this.openDeviceDialog(result.deviceId, result.deviceType);
          }

          if (result && result.openEditModal) {
            this.openCodeCreateEditDialog('edit', reservationCode);
          }

          // Refresh background data
          this.smartDeviceService.refreshReservationsAndDevicesFromApi$.next(null);
        });
      },
      catchError((error) => {
        console.error('Error fetching data', error);
        loadingRef.close();

        return of(false);
      })
    );
  }

  openCodeCreateEditDialog(mode: 'create' | 'edit' = 'create', reservationId?: Code['reservation']['reservation_id']) {
    this.dialog.open(DialogAddManualSmartCodeComponent, {
      ...this.dialogOptions,
      data: {
        mode,
        reservationId,
      },
    });
  }

  openDeviceDialog(deviceId: Device['id'], deviceType: Device['device_type']) {
    // Open loading indicator dialog
    const loadingRef: MatDialogRef<LoadingSpinnerComponent> = this.dialog.open(LoadingSpinnerComponent, {
      disableClose: true,
    });

    this.smartDeviceService.getDeviceDetails(deviceId, deviceType).subscribe(
      (res) => {
        loadingRef.close();

        // Refresh background data
        this.smartDeviceService.refreshReservationsAndDevicesFromApi$.next(null);

        // Open the main dialog with the fetched data
        const dialog = this.dialog.open(DialogDeviceDetailsComponent, {
          ...this.dialogOptions,
          data: {
            device: res,
          },
        });

        dialog.afterClosed().subscribe((result) => {
          if (result && result.openCodeModal) {
            this.openCodeDialog(result.reservationId);
          }

          if (result && result.openEditModal) {
            this.openDeviceEditDialog(result.device).subscribe();
          }

          // Refresh background data
          this.smartDeviceService.refreshReservationsAndDevicesFromApi$.next(null);
        });
      },
      (error) => {
        console.error('Error fetching data', error);
        loadingRef.close();
      }
    );
  }

  openThermostatDialog(deviceId: Device['id'], deviceType: Device['device_type']) {
    // Open loading indicator dialog
    const loadingRef: MatDialogRef<LoadingSpinnerComponent> = this.dialog.open(LoadingSpinnerComponent, {
      disableClose: true,
    });

    this.smartDeviceService.getDeviceDetails(deviceId, deviceType).subscribe(
      (res) => {
        loadingRef.close();

        // Refresh background data
        this.smartDeviceService.refreshReservationsAndDevicesFromApi$.next(null);

        // Open the main dialog with the fetched data
        const dialog = this.dialog.open(DialogDeviceDetailsComponent, {
          ...this.dialogOptions,
          data: {
            device: res,
          },
        });

        dialog.afterClosed().subscribe((result) => {
          if (result && result.openCodeModal) {
            this.openCodeDialog(result.reservationId);
          }

          if (result && result.openEditModal) {
            this.openDeviceEditDialog(result.device).subscribe();
          }

          // Refresh background data
          this.smartDeviceService.refreshReservationsAndDevicesFromApi$.next(null);
        });
      },
      (error) => {
        console.error('Error fetching data', error);
        loadingRef.close();
      }
    );
  }

  openDeviceEditDialog(device: MatchedDevice | UnmatchedDevice, currentNumber?: string) {
    const dialog = this.dialog.open(DialogEditDeviceComponent, {
      width: '100%',
      height: '100%',
      minWidth: '100%',
      maxHeight: '100%',
      data: {
        device,
        currentNumber: currentNumber || null,
      },
    });

    return dialog.afterClosed().pipe(
      tap(() => {
        // Refresh background data
        this.smartDeviceService.refreshReservationsAndDevicesFromApi$.next(null);
      })
    );
  }
}
